import React, { useState, useRef } from 'react';
import { 
  ShieldCheck, Download, Copy, Check, 
  AlertCircle, ArrowLeft, Sparkles, HelpCircle, FileText, Upload 
} from 'lucide-react';
import { LocalModelToggle } from './LocalModelToggle';

interface CommercialContractAuditorProps {
  language: 'uk' | 'en' | 'pl';
  onBack: () => void;
  onGoHome?: () => void;
  useEnterpriseContext?: boolean;
  enterpriseName?: string;
}

const LOADING_STEPS_UK = [
  "Ініціалізація аналітичного модуля аудиту договорів...",
  "Експерт з договірного права перевіряє відповідність Цивільному кодексу України...",
  "Спеціаліст з ризиків вивчає приховані комерційні та податкові аспекти...",
  "Старший юридичний радник формує таблицю рекомендацій Due Diligence..."
];

const LOADING_STEPS_EN = [
  "Initializing commercial contract audit AI agents...",
  "Contract law expert checks Ukrainian Civil Code (CCU) compliance...",
  "Risk manager is searching for hidden liabilities and tax pitfalls...",
  "Lead counsel compiles findings and drafts comparative redlines table..."
];

export default function CommercialContractAuditor({ language, onBack, onGoHome, useEnterpriseContext, enterpriseName }: CommercialContractAuditorProps) {
  const isUk = language === 'uk';
  const [contractText, setContractText] = useState(
    useEnterpriseContext ? (isUk ? 'База знань нашого підприємства буде автоматично залучена для перевірки дотримання інтересів нашої сторони.' : 'The company knowledge base will be automatically checked against the contract terms to protect our interests.') : ''
  );
  const [fileName, setFileName] = useState<string | null>(null);
  
  const [useLocalModel, setUseLocalModel] = useState<boolean>(() => {
    const saved = localStorage.getItem('prefer_local_model');
    return saved !== null ? saved === 'true' : true;
  });

  const [isExtracting, setIsExtracting] = useState(false);
  const [isAuditing, setIsAuditing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<string | null>(null);
  
  const [copiedReport, setCopiedReport] = useState(false);
  const [loadingStepIndex, setLoadingStepIndex] = useState(0);
  
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cycle loading steps
  React.useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isAuditing) {
      setLoadingStepIndex(0);
      const steps = isUk ? LOADING_STEPS_UK : LOADING_STEPS_EN;
      interval = setInterval(() => {
        setLoadingStepIndex((prev) => (prev < steps.length - 1 ? prev + 1 : prev));
      }, 7000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isAuditing, isUk]);

  // File Upload and Text Extraction
  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileName(file.name);
    setError(null);
    setIsExtracting(true);
    setReport(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await fetch('/api/extract-text', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || (isUk ? 'Помилка вилучення тексту з файлу.' : 'Failed to extract text from the file.'));
      }

      const data = await response.json();
      setContractText(data.text);
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isUk ? 'Не вдалося прочитати файл.' : 'Failed to read file.'));
      setFileName(null);
    } finally {
      setIsExtracting(false);
    }
  };

  const handleRun = async () => {
    if (!contractText.trim()) return;

    setIsAuditing(true);
    setError(null);
    setReport(null);

    try {
      const customKey = localStorage.getItem('custom_gemini_api_key');
      const headers: Record<string, string> = {
        'Content-Type': 'application/json'
      };
      if (customKey) {
        headers['x-api-key'] = customKey;
      }

      const response = await fetch('/api/run-templated-crew', {
        method: 'POST',
        headers,
        body: JSON.stringify({
          crewName: "Аудит господарських договорів в Україні",
          agentId: "commercial-contract-auditor",
          useLocalModel,
          inputs: {
            contract_text: contractText
          },
          useEnterpriseContext
        })
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data.error || (isUk ? 'Помилка аналізу договору.' : 'Failed to audit commercial contract.'));
      }

      const data = await response.json();
      if (data.jobId) {
        const pollJob = async () => {
          try {
            const statusRes = await fetch(`/api/crew-status/${data.jobId}`);
            if (!statusRes.ok) {
              throw new Error(isUk ? 'Помилка перевірки статусу завдання.' : 'Status check failed.');
            }
            const statusData = await statusRes.json();
            if (statusData.status === 'completed') {
              const rawReport = statusData.report || statusData.result || '';
              const resText = typeof rawReport === 'string'
                ? rawReport
                : (typeof rawReport === 'object' && rawReport !== null
                    ? (rawReport.report || rawReport.result || JSON.stringify(rawReport, null, 2))
                    : String(rawReport));

              if (!resText) {
                throw new Error(isUk ? 'Сервер повернув порожній результат звіту.' : 'Server returned empty report content.');
              }
              setReport(resText);
              setIsAuditing(false);
            } else if (statusData.status === 'failed') {
              setError(statusData.error || (isUk ? 'Помилка генерації звіту.' : 'Report generation failed.'));
              setIsAuditing(false);
            } else {
              setTimeout(pollJob, 2000);
            }
          } catch (pollErr: any) {
            console.error(pollErr);
            setError(pollErr.message || (isUk ? 'Помилка опитування завдання.' : 'Polling error.'));
            setIsAuditing(false);
          }
        };
        setTimeout(pollJob, 1000);
      } else {
        setReport(data.report || data.result || '');
        setIsAuditing(false);
      }
    } catch (err: any) {
      console.error(err);
      setError(err.message || (isUk ? 'Сталася помилка.' : 'An error occurred.'));
      setIsAuditing(false);
    }
  };

  const handleDownloadWord = async () => {
    if (!report) return;

    try {
      const name = fileName ? 'audit-' + fileName.split('.')[0] : 'commercial-contract-audit';
      const response = await fetch('/api/generate-docx', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: report,
          fileName: name
        })
      });

      if (!response.ok) throw new Error(isUk ? 'Не вдалося згенерувати Word файл.' : 'Failed to generate Word file.');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${name}.docx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      console.error(err);
      alert(err.message);
    }
  };

  const handleCopyReport = () => {
    if (!report) return;
    navigator.clipboard.writeText(report);
    setCopiedReport(true);
    setTimeout(() => setCopiedReport(false), 2000);
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto px-4 pb-12 text-slate-800">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-slate-200 pb-4">
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <button
              onClick={onGoHome || onBack}
              className="px-3.5 py-2 text-xs font-mono font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded transition-all cursor-pointer uppercase flex items-center gap-1.5"
            >
              🏠 {isUk ? "На головну" : "Home"}
            </button>
            <button
              onClick={onBack}
              className="px-3.5 py-2 text-xs font-mono font-bold text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-300 rounded transition-all cursor-pointer uppercase flex items-center gap-1.5"
            >
              <ArrowLeft className="w-4 h-4 text-blue-600" />
              <span>{isUk ? "← До Бізнес Центру" : "← Back to Business Hub"}</span>
            </button>
          </div>
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2">
              <ShieldCheck className="w-6 h-6 text-emerald-500" />
              <span>{isUk ? "Аудит господарських договорів" : "AI Commercial Contract Auditor"}</span>
            </h1>
            <p className="text-xs text-slate-500">
              {isUk ? "Аналіз комерційних договорів на відповідність Цивільному кодексу України та виявлення ризиків" : "Audits B2B contracts for compliance with the Civil Code of Ukraine and flags liabilities"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <LocalModelToggle
            isLocal={useLocalModel}
            onToggle={(enabled) => {
              setUseLocalModel(enabled);
              localStorage.setItem('prefer_local_model', enabled ? 'true' : 'false');
            }}
            language={language}
          />
        </div>
      </div>

      {useEnterpriseContext && (
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-3.5 flex items-center justify-between text-xs text-amber-800">
          <div className="flex items-center gap-2.5">
            <Sparkles className="w-4 h-4 text-amber-600 animate-pulse" />
            <div>
              <span className="font-bold">{isUk ? "Активовано контекст підприємства: " : "Enterprise Context Active: "}</span>
              <span className="font-mono">{enterpriseName || (isUk ? "База знань компанії" : "Company Knowledge Base")}</span>
            </div>
          </div>
          <span className="text-[10px] px-2 py-0.5 bg-amber-500/20 rounded text-amber-700 font-bold uppercase tracking-wider">
            {isUk ? "База знань" : "Knowledge Base"}
          </span>
        </div>
      )}

      {/* Main Form */}
      {!report && !isAuditing && (
        <div className="space-y-4">
          <button
            type="button"
            onClick={() => {
              setContractText(`ДОГОВІР ПРО НАДАННЯ IT-ПОСЛУГ № 88/2026
м. Львів, 10 лютого 2026 року

Замовник: ТОВ "Логістик Солюшнс"
Виконавець: ФОП Петренко Тарас Григорович

1. ПРЕДМЕТ ДОГОВОРУ
1.1. Виконавець зобов'язується надати послуги з розробки та підтримки програмного забезпечення, а Замовник - прийняти та оплатити їх.

2. ВАРТІСТЬ ПОСЛУГ ТА ОПЛАТА
2.1. Вартість послуг становить 120 000 грн на місяць.
2.2. Замовник має право затримати оплату на строк до 90 днів без сплати будь-якої пені у разі форс-мажорних обставин.

3. ВІДПОВІДАЛЬНІСТЬ СТОРІН ТА ШТРАФИ
3.1. У разі виявлення багів або затримки здачі етапу Виконавець сплачує Замовнику штраф у розмірі 50% від місячної вартості послуг та відшкодовує всі збитки.
3.2. Виконавець не має права розривати цей Договір в односторонньому порядку до закінчення терміну його дії.`);
              setError(null);
            }}
            className="w-full py-2.5 px-4 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200 rounded-xl text-emerald-800 font-mono text-xs font-bold transition-all cursor-pointer flex items-center justify-center gap-2 shadow-2xs"
          >
            <Sparkles className="w-4 h-4 text-emerald-600 animate-pulse" />
            <span>{isUk ? "⚡ Спробувати на демо-прикладі в 1 клік (Комерційний IT-договір)" : "⚡ Try 1-click Demo Sample (Commercial IT Contract)"}</span>
          </button>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="md:col-span-2 space-y-4">
            <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-4 shadow-xs">
              <div className="flex justify-between items-center">
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider">
                  {isUk ? "Текст договору для аудиту" : "Contract text for audit"}
                </label>
                {contractText && (
                  <button 
                    onClick={() => {
                      setContractText('');
                      setFileName(null);
                      if (fileInputRef.current) fileInputRef.current.value = '';
                    }}
                    className="text-xs text-slate-500 hover:text-red-500 transition-colors font-bold uppercase tracking-wider cursor-pointer"
                  >
                    {isUk ? "Очистити" : "Clear"}
                  </button>
                )}
              </div>
              <textarea
                placeholder={isUk 
                  ? "Вставте текст комерційного договору або завантажте файл (.pdf, .docx, .txt) нижче..." 
                  : "Paste the commercial contract text or upload a document (.pdf, .docx, .txt) below..."}
                value={contractText}
                onChange={(e) => setContractText(e.target.value)}
                rows={12}
                className="w-full p-4 bg-slate-550 border border-slate-200 rounded-xl focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500/30 text-sm focus:outline-none transition-colors placeholder-slate-400 leading-relaxed font-mono"
              />

              {/* Upload field */}
              <div className="flex items-center gap-4">
                <input 
                  type="file" 
                  ref={fileInputRef}
                  onChange={handleFileUpload}
                  className="hidden" 
                  accept=".pdf,.docx,.doc,.txt"
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-650 hover:text-slate-800 text-[10px] font-bold font-mono uppercase tracking-wider rounded-lg border border-slate-200 flex items-center gap-1.5 cursor-pointer transition-colors shadow-xs"
                >
                  <Upload className="w-3.5 h-3.5" />
                  <span>{isUk ? "Завантажити файл" : "Upload File"}</span>
                </button>
                {fileName && (
                  <span className="text-xs font-mono text-slate-500 italic truncate max-w-[200px]">
                    📎 {fileName}
                  </span>
                )}
                {isExtracting && (
                  <span className="text-xs font-mono text-slate-500 flex items-center gap-1 animate-pulse">
                    <span>⏳</span>
                    <span>{isUk ? "Зчитування тексту..." : "Extracting text..."}</span>
                  </span>
                )}
              </div>
            </div>

            <button
              onClick={handleRun}
              disabled={!contractText.trim() || isExtracting}
              className="w-full py-3 bg-emerald-500 hover:bg-emerald-650 disabled:bg-slate-100 disabled:text-slate-400 text-white font-black text-sm uppercase rounded-xl tracking-wider flex items-center justify-center gap-2 transition-colors cursor-pointer shadow-xs"
            >
              <ShieldCheck className="w-4 h-4" />
              <span>{isUk ? "Запустити аудит договору" : "Run Contract Audit"}</span>
            </button>
          </div>

          {/* Guidelines Sidebar */}
          <div className="space-y-4">
            <div className="bg-slate-550 border border-slate-200 rounded-xl p-5 space-y-4">
              <h3 className="text-xs font-bold text-slate-700 uppercase tracking-wider flex items-center gap-1.5 border-b border-slate-200 pb-2">
                <HelpCircle className="w-4 h-4 text-emerald-505" />
                <span>{isUk ? "Як це працює?" : "How it works?"}</span>
              </h3>
              <ul className="space-y-3 font-mono text-[10px] text-slate-650 leading-relaxed">
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 bg-emerald-100 text-emerald-650 font-bold rounded-full flex items-center justify-center shrink-0">1</span>
                  <span>{isUk ? "Юрист оцінює договір за нормами Цивільного кодексу України (ЦКУ)." : "Lawyer audits details strictly based on the Civil Code of Ukraine."}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 bg-emerald-100 text-emerald-650 font-bold rounded-full flex items-center justify-center shrink-0">2</span>
                  <span>{isUk ? "Ризик-менеджер знаходить податкові застереження, пені та валютні ризики." : "Risk Analyst spots tax liabilities, penalties imbalance, and force majeure."}</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="w-4 h-4 bg-emerald-100 text-emerald-650 font-bold rounded-full flex items-center justify-center shrink-0">3</span>
                  <span>{isUk ? "Провідний радник зводить все в таблицю порівняння правок." : "Lead Advisor details a redlines table with recommendations."}</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
        </div>
      )}

      {/* Loading State */}
      {isAuditing && (
        <div className="bg-white border border-slate-200 rounded-xl p-8 shadow-xs flex flex-col items-center justify-center min-h-[350px] space-y-6">
          <div className="relative w-16 h-16 flex items-center justify-center">
            <div className="absolute w-full h-full border-4 border-emerald-200 rounded-full animate-pulse" />
            <div className="absolute w-full h-full border-4 border-t-emerald-500 rounded-full animate-spin" />
            <ShieldCheck className="w-6 h-6 text-emerald-500" />
          </div>
          <div className="text-center space-y-2 max-w-md">
            <h3 className="text-sm font-bold uppercase tracking-wider text-slate-800">
              {isUk ? "Аудит комерційного договору..." : "Auditing contract..."}
            </h3>
            <p className="text-xs text-slate-500 font-mono italic leading-normal px-4">
              {isUk ? LOADING_STEPS_UK[loadingStepIndex] : LOADING_STEPS_EN[loadingStepIndex]}
            </p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-5 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <div className="space-y-1">
            <h4 className="text-xs font-bold text-red-800 uppercase tracking-wider">
              {isUk ? "Помилка аналізу" : "Execution Error"}
            </h4>
            <p className="text-xs text-red-650 leading-relaxed font-mono">{error}</p>
          </div>
        </div>
      )}

      {/* Report View */}
      {report && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-xs overflow-hidden">
          {/* Action Bar */}
          <div className="bg-slate-50 border-b border-slate-200 px-5 py-4 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <div className="bg-emerald-50 p-1.5 rounded border border-emerald-100">
                <FileText className="w-4 h-4 text-emerald-650" />
              </div>
              <span className="text-xs font-bold text-slate-800 font-mono uppercase tracking-wider">
                {isUk ? "Звіт комерційного аудиту" : "Commercial Due Diligence Report"}
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button 
                onClick={handleCopyReport}
                className="py-1.5 px-3 bg-white hover:bg-slate-50 text-slate-650 hover:text-slate-800 border border-slate-200 rounded-lg text-[10px] font-bold font-mono uppercase tracking-wider flex items-center gap-1 cursor-pointer transition-colors shadow-xs"
              >
                {copiedReport ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
                <span>{copiedReport ? (isUk ? 'Скопійовано' : 'Copied') : (isUk ? 'Копіювати' : 'Copy')}</span>
              </button>
              <button 
                onClick={handleDownloadWord}
                className="py-1.5 px-3 bg-[#1E3A8A] hover:bg-[#1E3A8A]/90 text-white rounded-lg text-[10px] font-bold font-mono uppercase tracking-wider flex items-center gap-1 cursor-pointer transition-colors shadow-xs"
              >
                <Download className="w-3.5 h-3.5" />
                <span>Word (.docx)</span>
              </button>
            </div>
          </div>

          {/* Report Content */}
          <div className="p-6 sm:p-8 prose prose-slate max-w-none text-xs leading-relaxed font-mono bg-slate-50 border-b border-slate-100 overflow-x-auto whitespace-pre-wrap text-slate-800">
            {report}
          </div>
          
          <div className="p-4 bg-white flex justify-end">
            <button
              onClick={() => {
                setReport(null);
                setContractText('');
                setFileName(null);
                if (fileInputRef.current) fileInputRef.current.value = '';
              }}
              className="py-2 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold font-mono text-[10px] uppercase rounded-lg tracking-wider cursor-pointer transition-colors"
            >
              {isUk ? "Новий аудит договору" : "New Audit"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
